import React, { useState, useEffect } from 'react';
import { X, Search, Loader2, Filter, Check } from 'lucide-react';
import { Combobox } from './Combobox';
import { fetchMachines, fetchCategorie, CategoriaOption } from '../api';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useAbbinatoreFacets } from '../hooks/useAbbinatoreFacets';
import {
  Popover as PopoverRoot,
  PopoverTrigger as PopoverTriggerRoot,
  PopoverContent as PopoverContentRoot,
} from '../../../../components/ui/popover';

const Popover = PopoverRoot as React.FC<{ open?: boolean; onOpenChange?: (o: boolean) => void; children?: React.ReactNode }>;
const PopoverTrigger = PopoverTriggerRoot as React.FC<{ asChild?: boolean; children?: React.ReactNode }>;
const PopoverContent = PopoverContentRoot as React.FC<{ className?: string; align?: 'start' | 'center' | 'end'; sideOffset?: number; children?: React.ReactNode }>;

export interface RebrandSelection { id: string; brand: string; model: string }

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  /** IDs macchine da escludere dai risultati (es. se stessa, già figli) */
  excludeIds: string[];
  /** Se true → checkbox + bottone Conferma finale (aggiunta figli).
   *  Se false (default) → bottone "Seleziona" per riga (impostazione padre). */
  multiSelect?: boolean;
  /** Se true → nasconde macchine che sono già padri rebrand (ha senso solo per aggiunta figli) */
  excludeParents?: boolean;
  /** Callback: selezioni + categorie escluse */
  onConfirm: (selections: RebrandSelection[], categorieEscluse: string[]) => Promise<void>;
}

export default function AddRebrandModal({
  open, onClose, title, excludeIds,
  multiSelect = false, excludeParents = false, onConfirm,
}: Props) {
  const [filterBrand, setFilterBrand] = useState<string | null>(null);
  const [filterTipo, setFilterTipo] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState('');
  const debouncedSearch = useDebounce(filterSearch, 280);

  // Tipologie ristrette alla marca scelta.
  const { marche, tipologie } = useAbbinatoreFacets('machines', filterBrand, {
    value: filterTipo, onChange: setFilterTipo,
  });

  const [results, setResults] = useState<{ id: string; brand: string; model: string; type: string | null }[]>([]);
  const [loading, setLoading] = useState(false);

  const [categorieDisp, setCategorieDisp] = useState<CategoriaOption[]>([]);
  const [categorieEscluse, setCategorieEscluse] = useState<string[]>([]);
  const [catOpen, setCatOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  /* Selezione multipla: Map<id, { brand, model }> */
  const [selectedItems, setSelectedItems] = useState<Map<string, { brand: string; model: string }>>(new Map());

  /* Carica categorie una volta all'apertura */
  useEffect(() => {
    if (!open) return;
    fetchCategorie()
      .then(setCategorieDisp)
      .catch(() => setCategorieDisp([]));
  }, [open]);

  /* Ricerca macchine (debounced) */
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchMachines({
      search: debouncedSearch || undefined,
      brand: filterBrand || undefined,
      tipo: filterTipo || undefined,
      excludeParents: excludeParents || undefined,
      pageSize: 40,
    })
      .then(r => setResults(
        r.items
          .filter(m => !excludeIds.includes(m.id))
          .map(m => ({ id: m.id, brand: m.brand, model: m.model, type: m.type })),
      ))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [open, filterBrand, filterTipo, debouncedSearch]); // eslint-disable-line

  /* Reset all'apertura */
  useEffect(() => {
    if (open) {
      setFilterBrand(null); setFilterTipo(null); setFilterSearch('');
      setCategorieEscluse([]); setBusy(false);
      setSelectedItems(new Map());
    }
  }, [open]);

  /* ── Handlers ── */
  async function handleSelectSingle(m: { id: string; brand: string; model: string }) {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm([m], categorieEscluse);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  function toggleItem(m: { id: string; brand: string; model: string }) {
    setSelectedItems(prev => {
      const next = new Map(prev);
      if (next.has(m.id)) {
        next.delete(m.id);
      } else {
        next.set(m.id, { brand: m.brand, model: m.model });
      }
      return next;
    });
  }

  async function handleConfirmMulti() {
    if (busy || selectedItems.size === 0) return;
    setBusy(true);
    try {
      const selections: RebrandSelection[] = Array.from(selectedItems.entries()).map(
        ([id, m]) => ({ id, brand: m.brand, model: m.model }),
      );
      await onConfirm(selections, categorieEscluse);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  const toggleCat = (code: string) =>
    setCategorieEscluse(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code],
    );

  if (!open) return null;

  const colClass = multiSelect
    ? 'grid-cols-[28px_1fr_1.5fr_1fr]'
    : 'grid-cols-[1fr_1.5fr_1fr_100px]';

  const headerCols = multiSelect
    ? ['', 'MARCA', 'MODELLO', 'TIPOLOGIA']
    : ['MARCA', 'MODELLO', 'TIPOLOGIA', ''];

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '80vh' }}>

        {/* Header */}
        <div className="flex-none px-5 py-3.5 border-b border-[#e4e4e1] flex items-center gap-3">
          <h3 className="text-[16px] font-semibold text-[#1f1f1d] flex-1">{title}</h3>
          <button onClick={onClose} className="text-[#5c5c57] hover:text-[#3a3a36]"
            data-testid="add-rebrand-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Filtri */}
        <div className="flex-none px-5 py-3 border-b border-[#f0f0ed] flex items-end gap-2.5 flex-wrap">
          <div className="w-40">
            <Combobox label="Marca" value={filterBrand} onChange={setFilterBrand}
              options={marche} placeholder="Tutte" testId="add-rebrand-brand" />
          </div>
          <div className="w-40">
            <Combobox label="Tipo" value={filterTipo} onChange={setFilterTipo}
              options={tipologie} placeholder="Tutti" testId="add-rebrand-tipo" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <div className="text-[13px] font-semibold text-[#5c5c57] mb-1.5">Cerca modello</div>
            <div className="flex items-center gap-1.5 px-2.5 py-[7px] border border-[#d8d8d4] rounded-[7px] bg-white">
              <Search className="w-3 h-3 text-[#5c5c57] flex-none" />
              <input value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
                placeholder="Codice o nome…"
                className="flex-1 text-[14px] outline-none bg-transparent placeholder:text-[#5c5c57]" />
            </div>
          </div>

          {/* Categorie escluse */}
          <div>
            <div className="text-[13px] font-semibold text-[#5c5c57] mb-1.5">Cat. escluse</div>
            <Popover open={catOpen} onOpenChange={setCatOpen}>
              <PopoverTrigger asChild>
                <button data-testid="add-rebrand-cat-escluse-btn"
                  className={`px-3 py-[7px] rounded-[7px] border text-[14px] font-semibold flex items-center gap-1.5 transition-colors ${
                    categorieEscluse.length > 0
                      ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
                      : 'bg-white border-[#d8d8d4] text-[#3a3a36] hover:border-[#b8b8b3]'
                  }`}>
                  <Filter className="w-3.5 h-3.5" />
                  {categorieEscluse.length > 0 ? `${categorieEscluse.length} escluse` : 'Filtra'}
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-3 w-64" align="end" sideOffset={4}>
                <div className="text-[12px] font-bold text-[#5c5c57] mb-2 uppercase tracking-wider font-mono">
                  Categorie da escludere
                </div>
                <div className="max-h-52 overflow-y-auto space-y-0.5">
                  {categorieDisp.length === 0 ? (
                    <div className="text-[13px] text-[#5c5c57]">Caricamento…</div>
                  ) : (
                    categorieDisp.map(cat => {
                      const checked = categorieEscluse.includes(cat.code);
                      return (
                        <label key={cat.code}
                          className="flex items-center gap-2 py-1 px-1 cursor-pointer hover:bg-[#f3f3f0] rounded">
                          <span className={`w-3.5 h-3.5 border rounded flex items-center justify-center flex-none ${
                            checked ? 'bg-amber-500 border-amber-500' : 'border-[#d8d8d4]'
                          }`}>
                            {checked && <Check className="w-2.5 h-2.5 text-white" />}
                          </span>
                          <input type="checkbox" className="sr-only" checked={checked}
                            onChange={() => toggleCat(cat.code)} />
                          <span className="text-[14px] text-[#3a3a36]">{cat.label || cat.code}</span>
                        </label>
                      );
                    })
                  )}
                </div>
                {categorieEscluse.length > 0 && (
                  <button onClick={() => setCategorieEscluse([])}
                    className="mt-2 text-[12px] text-[#8a3b3b] hover:underline">
                    Svuota selezione
                  </button>
                )}
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Intestazione tabella */}
        <div className={`flex-none px-5 py-2 bg-[#fafafa] border-b border-[#e4e4e1] grid ${colClass} gap-3`}>
          {headerCols.map((h, i) => (
            <div key={i} className="text-[11px] font-bold tracking-[0.08em] text-[#5c5c57] font-mono">{h}</div>
          ))}
        </div>

        {/* Risultati */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="px-5 py-10 flex items-center justify-center gap-2 text-[14px] text-[#5c5c57]">
              <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
            </div>
          ) : results.length === 0 ? (
            <div className="px-5 py-10 text-center text-[14px] text-[#5c5c57]">Nessun risultato.</div>
          ) : (
            results.map(m => {
              const isSelected = selectedItems.has(m.id);
              return (
                <div key={m.id} data-testid={`add-rebrand-row-${m.id}`}
                  className={`grid ${colClass} gap-3 px-5 py-2.5 border-b border-[#f1f1ee] last:border-0
                    items-center transition-colors ${
                    multiSelect
                      ? `cursor-pointer ${isSelected ? 'bg-[#eef1fb]' : 'hover:bg-[#fafafa]'}`
                      : 'hover:bg-[#fafafa]'
                  }`}
                  onClick={multiSelect ? () => toggleItem(m) : undefined}
                >
                  {/* Checkbox (solo multi-select) */}
                  {multiSelect && (
                    <div className="flex items-center justify-center">
                      <span className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-none transition-colors ${
                        isSelected
                          ? 'bg-[#2b3a8f] border-[#2b3a8f]'
                          : 'border-[#c8c8c4] hover:border-[#2b3a8f]'
                      }`}>
                        {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                      </span>
                    </div>
                  )}

                  <div className="text-[14px] text-[#5c5c57] truncate">{m.brand}</div>
                  <div className="text-[14px] font-semibold text-[#1f1f1d] truncate">{m.model}</div>
                  <div className="text-[13px] text-[#6d6d68] truncate">
                    {tipologie.find(t => t.code === m.type)?.label ?? m.type ?? '—'}
                  </div>

                  {/* Bottone Seleziona (solo single-select) */}
                  {!multiSelect && (
                    <div className="flex justify-end">
                      <button onClick={() => handleSelectSingle(m)} disabled={busy}
                        data-testid={`add-rebrand-select-${m.id}`}
                        className="px-2.5 py-1.5 rounded-[7px] bg-[#2b3a8f] text-white text-[13px]
                          font-semibold hover:bg-[#243279] disabled:opacity-50 transition-colors">
                        Seleziona
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer: bottone Conferma (solo multi-select) */}
        {multiSelect && (
          <div className="flex-none px-5 py-3 border-t border-[#e4e4e1] bg-[#fafafa]
            flex items-center justify-between gap-3">
            <span className="text-[14px] text-[#5c5c57]">
              {selectedItems.size > 0 ? (
                <>
                  {selectedItems.size} modell{selectedItems.size === 1 ? 'o selezionato' : 'i selezionati'}
                </>
              ) : (
                <span className="text-[#5c5c57]">Nessun modello selezionato</span>
              )}
            </span>
            <button
              onClick={handleConfirmMulti}
              disabled={busy || selectedItems.size === 0}
              data-testid="add-rebrand-confirm-multi"
              className="px-4 py-2 rounded-[7px] bg-[#2b3a8f] text-white text-[14px] font-semibold
                hover:bg-[#243279] disabled:opacity-40 flex items-center gap-2 transition-colors"
            >
              {busy
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Check className="w-3.5 h-3.5" />
              }
              Conferma selezione
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
